/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { createSlice } from '@reduxjs/toolkit';
import { type LoanPayment } from '../../Customer/store/customerTypes';
import { getLoanTypes, getUndisbursedLoans, getUnpledgedLoans, getUnapprovedLoans, getUnsettledPayments, getRecoveryLoans, getUnapprovedPayments, getReports } from './loanMiddleware';
import { type Metal, type InterestType } from './loanTypes';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'
import ExcelJS from 'exceljs';
import fmt from 'indian-number-format';

const generatePDF = (loanArray: any, loanTitle: string) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: 'landscape' });

  doc.setFontSize(18);

  doc.text(loanTitle, 14, 22);

  const startY = 30;

  const headers = [['S.No', 'Loan Number', 'Customer', 'Phonenumber', 'Phonenumber 2',
    'Start Date', 'Interest', 'Loan Average', 'Today Average', 'Till Date', 'Principal', 'Part Principal', 'Part Interest', 'Calculated Interest', 'Days Difference', 'Remarks']];

  const body = loanArray.map((loan: any) => [
    loan['S.No'],
    loan.loanNumber,
    loan.customerName,
    // loan.customerId,
    loan.customerPhone,
    loan.customerPhone2,
    loan.startDate,
    loan.interestType,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    loan.loanAverage !== null ? parseInt(loan.loanAverage) : 'N/A',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    loan.todayAverage !== null ? parseInt(loan.todayAverage) : 'N/A',
    loan.tillDate,
    loan.principal,
    loan.paidPrincipal,
    loan.paidInterest,
    loan.calculatedInterest,
    loan.dateDifference,
    ''
  ]);

  autoTable(doc, {
    startY,
    theme: 'grid',
    head: headers,
    body,
    columnStyles: {
      [headers[0].length - 1]: { cellWidth: 24 },
    },
    styles: {
      cellPadding: 3,
      fontSize: 6,
      valign: 'middle',
      halign: 'center',
    },
  });
  doc.save('loan-recovery.pdf');
};

const generateLoanExcel = async (reportData: any, branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();

  // Create Ledger Worksheet
  const reportSheet = workbook.addWorksheet('Interest accured report');
  // const headers = Object.keys(flattenedData[2]);

  const organizationName = localStorage.getItem('organizationName')?? '';
  reportSheet.mergeCells('A1:M1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left' };

  let headerText = '';
  if (branch?? (startDate?? endDate)) {
    headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:M2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left' };
  }

  const headers = ['S.No', 'Loan ID', 'Date of Loan', 'Branch', 'Borrower Name', 'Loan Amount', 'Outstanding Loan Amount', 'Scheme', 'Interest as per Scheme', 'Current Interest Rate', 'Interest Days', 'Interest accrued', 'Penal Interest'];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  reportSheet.columns = headers.map(() => ({
    width: 25,
  }));

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  reportData.forEach((row:any) => reportSheet.addRow(Object.values(row))); 

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if(rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  // Save as Blob
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'interest_accrued_report.xlsx';
  link.click();
};
const generatePledgeExcel = async (reportData: any, branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();

  // Create Ledger Worksheet
  const reportSheet = workbook.addWorksheet('Interest payable report');
  // const headers = Object.keys(flattenedData[2]);

  // Organization name row
  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:M1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  // Header text row
  let headerText = '';
  if (branch?? (startDate?? endDate)) {
    headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:M2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = ['S.No', 'Loan ID', 'Date of Loan', 'Branch', 'Borrower Name', 'Refinance Amount', 'Outstanding amount', 'Scheme', 'Interest as per Scheme', 'Current Interest Rate', 'Interest Days', 'Interest Payable', 'Penal Interest'];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  reportData.forEach((row:any) => reportSheet.addRow(Object.values(row))); 

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if(rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({
    width: 25,
  }));

  // Save as Blob
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'interest_payable_report.xlsx';
  link.click();
};
const generateDailyExcel = async (reportData: any, branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();

  // Create Ledger Worksheet
  const reportSheet = workbook.addWorksheet('Daily collection report');
  // const headers = Object.keys(flattenedData[2]);

  // Organization name row
  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:I1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  // Header text row
  let headerText = '';
  if (branch?? (startDate?? endDate)) {
    headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:I2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  // Column order here differs from the API's key order, so cells are addressed by
  // key — positional writes silently shift every column when the API shape changes
  const columnDefs: Array<{ header: string; key: string }> = [
    { header: 'Loan ID', key: 'loanNumber' },
    { header: 'Branch', key: 'branch' },
    { header: 'Borrower Name', key: 'customerName' },
    { header: 'Amount Collected', key: 'amountCollected' },
    { header: 'Principal Component', key: 'principalCollected' },
    { header: 'Online Principal', key: 'onlinePrincipal' },
    { header: 'Offline Principal', key: 'offlinePrincipal' },
    { header: 'Interest Component', key: 'interestCollected' },
    { header: 'Online Interest', key: 'onlineInterest' },
    { header: 'Offline Interest', key: 'offlineInterest' },
    { header: 'Reducing Interest', key: 'reducingInterest' },
    { header: 'Penal Interest/Others', key: 'penalInterest' },
    { header: 'Collection Mode', key: 'collectionMode' },
    { header: 'Remarks', key: 'remarks' },
    { header: 'Principal Outstanding', key: 'principalOutstanding' },
    { header: 'Interest Outstanding', key: 'interestOutstanding' },
  ];
  const headers = columnDefs.map((c) => c.header);
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  // Amount columns are totalled at the foot of the sheet; the rest stay blank there
  const totalledKeys = [
    'amountCollected',
    'principalCollected',
    'onlinePrincipal',
    'offlinePrincipal',
    'interestCollected',
    'onlineInterest',
    'offlineInterest',
    'reducingInterest',
    'penalInterest',
    'principalOutstanding',
    'interestOutstanding',
  ];
  const totals: Record<string, number> = {};
  totalledKeys.forEach((key) => { totals[key] = 0; });

  reportData.forEach((row: any) => {
    reportSheet.addRow(columnDefs.map((c) => row[c.key] ?? ''));
    totalledKeys.forEach((key) => {
      totals[key] += Number(row[key] ?? 0);
    });
  });

  const totalsRow = reportSheet.addRow(
    columnDefs.map((c, index) => {
      if (index === 2) return 'Total';
      if (totalledKeys.includes(c.key)) return parseFloat(totals[c.key].toFixed(2));
      return '';
    })
  );
  totalsRow.font = { bold: true };
  // includeEmpty so the highlight runs across the blank label columns too
  totalsRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if(rowIndex > 2){
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({
    width: 25,
  }));

  // Save as Blob
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'daily_collection_report.xlsx';
  link.click();
};
const generateActiveExcel = async (reportData: any, branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Active Loan Report');

  const part1Headers = [
    'Branch',
    'Total No Of Active Loans',
    'Total Principal Outstanding',
    'Total Interest Outstanding',
    'Total Loan Outstanding',
  ];

  const part2Headers = [
    'Branch',
    'Total No Of Loans Refinanced',
    'Total Principal Outstanding of Loan Refinanced',
    'Total Interest of Loan Refinanced',
    'Total Loan Outstanding of Loan Refinanced',
  ];

  reportSheet.columns = new Array(part2Headers.length).fill({ width: 30 });


  let currentRow = 1;
  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = organizationName;
  reportSheet.getCell(`A${currentRow}`).font = { bold: true };
  reportSheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left' };
  currentRow += 1;

  const asOnDate = endDate? new Date(endDate).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN');
  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = `As on Date: ${asOnDate}`;
  reportSheet.getCell(`A${currentRow}`).font = { bold: true };
  reportSheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left' };
  currentRow += 2;

  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = 'Dashboard';
  reportSheet.getCell(`A${currentRow}`).font = { bold: true };
  currentRow++;

  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = 'Branchwise';
  reportSheet.getCell(`A${currentRow}`).font = { bold: true, italic: true };
  currentRow++;

  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = 'Loan';
  reportSheet.getCell(`A${currentRow}`).font = { bold: true };
  currentRow++;

  reportSheet.addRow(part1Headers);
  reportSheet.getRow(currentRow).font = { bold: true };
  currentRow++;

  const part1Data = reportData.filter((item: any) => item.part === 'Part-1');
  part1Data.forEach((row: any) => {
    reportSheet.addRow([
      row.branch,
      row.totalActiveLoans || 0,
      fmt.format(row.totalPrincipalOutstanding || 0),
      fmt.format(row.totalInterestOutstanding || 0),
      fmt.format(row.totalLoanOutstanding || 0),
    ]);
    currentRow++;
  });

  currentRow++;

  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = 'Branchwise';
  reportSheet.getCell(`A${currentRow}`).font = { bold: true, italic: true };
  currentRow++;

  reportSheet.mergeCells(`A${currentRow}:E${currentRow}`);
  reportSheet.getCell(`A${currentRow}`).value = 'Refinance';
  reportSheet.getCell(`A${currentRow}`).font = { bold: true };
  currentRow++;

  reportSheet.addRow(part2Headers);
  reportSheet.getRow(currentRow).font = { bold: true };
  currentRow++;

  const part2Data = reportData.filter((item: any) => item.part === 'Part-2');
  part2Data.forEach((row: any) => {
    reportSheet.addRow([
      row.branch,
      row.totalRefinancedLoans || 0,
      fmt.format(row.totalPrincipalOutstandingRefinanced || 0),
      fmt.format(row.totalInterestOutstandingRefinanced || 0),
      fmt.format(row.totalLoanOutstandingRefinanced || 0),
    ]);
    currentRow++;
  });

  reportSheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'active_loan_report.xlsx';
  link.click();
};
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
const generateRefinanceExcel = async (reportData: any[], branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Refinance Report');

  // Organization name row
  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:O1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  // Header text row
  let headerText = '';
  if (branch ?? (startDate ?? endDate)) {
    headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:O2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = [
    'Loan ID',
    'Branch',
    'Borrower Name',
    'Date of Original Loan',
    'Amount of Original Loan',
    'Pledge ID',
    'Refinanced Amount',
    'Refinanced Date',
    'Current Principal Outstanding',
    'Current Interest Outstanding',
    'Total Outstanding',
    'Next Due Date',
    'Last Paid Date',
    'Last Paid Amount',
    'Default Days'
  ];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };


  reportData.forEach((row: any) => {
    const pledgeNumbersFormatted = Array.isArray(row.pledgeNumbers)
      ? row.pledgeNumbers.map((d: string) => d).join(', ')
      : row.pledgeNumbers;
    // Handle arrays for refinancedDate, nextDueDate, lastPaidDate, defaultDays
    const refinancedDateFormatted = Array.isArray(row.refinancedDate)
      ? row.refinancedDate.map((d: string) => formatDate(d)).join(', ')
      : formatDate(row.refinancedDate);

    const nextDueDateFormatted = Array.isArray(row.nextDueDate)
      ? row.nextDueDate.map((d: string) => formatDate(d)).join(', ')
      : formatDate(row.nextDueDate);

    const lastPaidDateFormatted = Array.isArray(row.lastPaidDate)
      ? row.lastPaidDate.map((d: string | null) => formatDate(d)).join(', ')
      : formatDate(row.lastPaidDate);

    const defaultDaysFormatted = Array.isArray(row.defaultDays)
      ? row.defaultDays.join(', ')
      : row.defaultDays;

    reportSheet.addRow([
      row.loanId,
      row.branch,
      row.borrowerName,
      formatDate(row.dateOfOriginalLoan),
      row.amountOfOriginalLoan,
      pledgeNumbersFormatted,
      row.refinancedAmount,
      refinancedDateFormatted,
      row.currentPrincipalOutstanding,
      row.currentInterestOutstanding,
      row.totalOutstanding,
      nextDueDateFormatted,
      lastPaidDateFormatted,
      row.lastPaidAmount,
      defaultDaysFormatted
    ]);
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if(rowIndex>2){
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({
    width: 25,
  }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'refinance_report.xlsx';
  link.click();
};

const generateExpenseExcel = async (reportData: any[], branch?: string, startDate?:string, endDate?:string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Expense Report');

  // Organization name row
  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:E1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  // Header text row
  let headerText = '';
  if (branch ?? (startDate ?? endDate)) {
    headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:E2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = [
    'Date',
    'Branch',
    'Expenses Head',
    'Amount',
    'Remarks'
  ];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };


  reportData.forEach((row: any) => {

    const dateFormatted = formatDate(row.date);

    reportSheet.addRow([
      dateFormatted,
      row.branch,
      row.type,
      row.amount,
      row.description
    ]);
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if(rowIndex > 2){
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({
    width: 25,
  }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'expense_tracker_report.xlsx';
  link.click();
};


const generateGSTR1Excel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('GSTR1 Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:I1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:I2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = ['S.No', 'Branch', 'Bill No', 'Bill Date', 'Bill Value', 'GST %', 'Taxable Value', 'CGST', 'SGST'];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalBillValue = 0;
  let totalTaxableValue = 0;
  let totalCgst = 0;
  let totalSgst = 0;

  reportData.forEach((row: any) => {
    reportSheet.addRow([
      row.serialNumber,
      row.branch,
      row.billNo,
      formatDate(row.billDate),
      parseFloat((row.billValue || 0).toFixed(2)),
      `${row.gstRate}%`,
      parseFloat((row.taxableValue || 0).toFixed(2)),
      parseFloat((row.cgst || 0).toFixed(2)),
      parseFloat((row.sgst || 0).toFixed(2)),
    ]);
    totalBillValue += row.billValue || 0;
    totalTaxableValue += row.taxableValue || 0;
    totalCgst += row.cgst || 0;
    totalSgst += row.sgst || 0;
  });

  const totalsRow = reportSheet.addRow(['', '', 'Total', '', parseFloat(totalBillValue.toFixed(2)), '', parseFloat(totalTaxableValue.toFixed(2)), parseFloat(totalCgst.toFixed(2)), parseFloat(totalSgst.toFixed(2))]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 20 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'gstr1_report.xlsx';
  link.click();
};

const generateCashBookExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Cash Bank Book');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:J1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:J2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = [
    'Date',
    'Transaction Type',
    'Voucher/Bill No',
    'Narration',
    'Offline Payment (Out)',
    'Offline Receipt (In)',
    'Offline Opening Balance',
    'Online Payment (Out)',
    'Online Receipt (In)',
    'Online Opening Balance',
  ];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  reportData.forEach((row: any) => {
    const dataRow = reportSheet.addRow([
      row.date ? formatDate(row.date) : '',
      row.transactionType,
      row.voucherNo || '',
      row.narration || '',
      row.offlineOut ?? '',
      row.offlineIn ?? '',
      row.offlineBalance,
      row.onlineOut ?? '',
      row.onlineIn ?? '',
      row.onlineBalance,
    ]);
    if (row.rowType === 'opening' || row.rowType === 'closing') {
      dataRow.font = { bold: true };
      dataRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
      });
    }
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 25 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'cash_bank_book.xlsx';
  link.click();
};

const generateCashFlowExcel = async (
  reportData: any[],
  branch?: string,
  startDate?: string,
  endDate?: string
) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Cash Flow Report');
 
  const organizationName = localStorage.getItem('organizationName') ?? '';
 
  reportSheet.mergeCells('A1:D1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };
 
  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells('A2:D2');
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }
 
  const headers = ['Code', 'Particulars', 'Cash (₹)', 'Bank (₹)'];
  const headerRowIndex = 3;
  const headerRow = reportSheet.getRow(headerRowIndex);
  headerRow.values = headers;
  headerRow.font = { bold: true };
  headerRow.eachCell((cell) => {
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
  });
 
  reportSheet.columns = [
    { width: 10 }, 
    { width: 48 }, 
    { width: 20 },  
    { width: 20 },  
  ];
 
  const dataRows = reportData.filter(
    (item: any) => item.Title !== '' || item.Code !== ''
  );
 
  dataRows.forEach((item: any) => {
    const isSectionHeader =
      item.Code && !String(item.Code).includes('.') && item.Cash === '' && item.Bank === '';
    const isSummary =
      !item.Code && (item.Title === 'Total Outflow' || item.Title === 'Derived Balance' || item.Title === "Difference" || item.Title === 'Total Inflow' || item.Title === 'Closing Balance');
 
    const cashVal = item.Cash === '' || item.Cash === null ? null : Number(item.Cash);
    const bankVal = item.Bank === '' || item.Bank === null ? null : Number(item.Bank);
 
    const dataRow = reportSheet.addRow([
      item.Code,
      item.Title,
      isSectionHeader ? null : cashVal,
      isSectionHeader ? null : bankVal,
    ]);
 
    const depth = item.Code ? String(item.Code).split('.').length - 1 : 0;
    dataRow.getCell(2).alignment = { horizontal: 'left', vertical: 'middle', indent: depth };
    dataRow.getCell(3).alignment = { horizontal: 'right', vertical: 'middle' };
    dataRow.getCell(4).alignment = { horizontal: 'right', vertical: 'middle' };
 
    if (isSectionHeader) {
      dataRow.font = { bold: true };
    } else if (isSummary) {
      dataRow.font = { bold: true };
      if (item.Title === 'Derived Balance') {
        if (cashVal !== null && cashVal < 0) {
          dataRow.getCell(3).font = { bold: true };
        }
        if (bankVal !== null && bankVal < 0) {
          dataRow.getCell(4).font = { bold: true };
        }
      }
    }
  });
 
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `cash_flow_report${startDate && endDate ? `_${startDate}_${endDate}` : ''}.xlsx`;
  link.click();
};

interface InitialState {
  metal: Metal[];
  type: InterestType[];
  unpledgedLoans: string[];
  undisbursedLoans: string[];
  unapprovedLoans: string[];
  unsettledPayments: LoanPayment[];
  unapprovedPayments: LoanPayment[];
  reportData: any;
}

const initialState: InitialState = {
  metal: [],
  type: [],
  unpledgedLoans: [],
  undisbursedLoans: [],
  unapprovedLoans: [],
  unsettledPayments: [],
  unapprovedPayments: [],
  reportData: [],
};

const generateAverageRateExcel = async (reportData: any[], branch?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Average Rate Tracker');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const colCount = 14;
  const lastCol = String.fromCharCode(64 + colCount);
  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch) {
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = `Branch: ${branch}`;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headers = [
    'S.No',
    'Loan Number',
    'Customer Name',
    'Phone',
    'Loan Start Date',
    'Last Payment Date',
    'Original Principal (₹)',
    'Pending Principal (₹)',
    'Pending Interest (₹)',
    'Metal',
    'Net Weight (g)',
    "Today's Gold Rate (₹/g)",
    'Live Loan Average (₹/g)',
    'Status',
  ];
  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  const aboveAverage = reportData.filter((r: any) => r.highlight === 'green').length;
  const belowAverage = reportData.filter((r: any) => r.highlight === 'red').length;

  reportData.forEach((row: any, idx: number) => {
    const dataRow = reportSheet.addRow([
      idx + 1,
      row.loanNumber,
      row.customerName,
      row.customerPhone,
      formatDate(row.startDate),
      formatDate(row.lastPaymentDate),
      row.originalPrincipal,
      row.pendingPrincipal,
      row.pendingInterest,
      row.metal,
      row.netWeight,
      row.todayGoldRate,
      row.liveLoanAverage,
      row.highlight === 'green' ? 'Safe' : 'At Risk',
    ]);
    const statusCell = dataRow.getCell(14);
    statusCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: row.highlight === 'green' ? 'FFD4EDDA' : 'FFF8D7DA' },
    };
    statusCell.font = { color: { argb: row.highlight === 'green' ? 'FF155724' : 'FF721C24' }, bold: true };
  });

  reportSheet.addRow([]);

  const aboveRow = reportSheet.addRow(['', '', '', '', '', '', '', '', '', '', '', '', 'Above Average', aboveAverage]);
  aboveRow.getCell(13).font = { bold: true };
  aboveRow.getCell(14).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD4EDDA' } };
  aboveRow.getCell(14).font = { bold: true, color: { argb: 'FF155724' } };

  const belowRow = reportSheet.addRow(['', '', '', '', '', '', '', '', '', '', '', '', 'Below Average', belowAverage]);
  belowRow.getCell(13).font = { bold: true };
  belowRow.getCell(14).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8D7DA' } };
  belowRow.getCell(14).font = { bold: true, color: { argb: 'FF721C24' } };

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 24 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'average_rate_tracker.xlsx';
  link.click();
};

const generateIncentivesExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
};

const generateStockExcel = async (reportData: any[], branch?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Stock Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const headers = [
    'S.No',
    'Branch',
    'Loan Number',
    'Loan Date',
    'Gross Weight (g)',
    'Net Weight (g)',
    'Value (₹/g)',
    'Total Value (₹)',
    'Hold Days',
  ];
  const colCount = headers.length;
  const lastCol = String.fromCharCode(64 + colCount);

  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch) {
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = `Branch: ${branch}`;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalGrossWeight = 0;
  let totalNetWeight = 0;
  let totalStockValue = 0;

  reportData.forEach((row: any, idx: number) => {
    reportSheet.addRow([
      idx + 1,
      row.branch,
      row.loanNumber,
      formatDate(row.loanDate),
      row.grossWeight,
      row.netWeight,
      row.value,
      row.totalValue,
      row.holdDays,
    ]);
    totalGrossWeight += row.grossWeight || 0;
    totalNetWeight += row.netWeight || 0;
    totalStockValue += row.totalValue || 0;
  });

  // Value (₹/g) is a rate, so it is left blank rather than summed.
  const totalsRow = reportSheet.addRow([
    '',
    '',
    'Total',
    '',
    parseFloat(totalGrossWeight.toFixed(3)),
    parseFloat(totalNetWeight.toFixed(3)),
    '',
    parseFloat(totalStockValue.toFixed(2)),
    '',
  ]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
      if (rowIndex === 1) {
        cell.font = { bold: true };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 20 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'stock_report.xlsx';
  link.click();
};

const LOAN_TYPE_LABEL: Record<string, string> = {
  direct: 'Direct',
  takeover: 'Takeover',
  repledge: 'Repledge',
};

const generateOrganizationLoanExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Organization Loan Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const headers = [
    'S.No',
    'Date',
    'Branch',
    'Loan Number',
    'Customer Name',
    'Loan Type',
    'G.W',
    'N.W',
    'Loan Amount',
    'DC',
    'GST',
    'Scheme',
    'Online',
    'Offline',
    'Loan Average',
    'Remarks',
  ];
  const lastCol = String.fromCharCode(64 + headers.length);

  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalGrossWeight = 0;
  let totalNetWeight = 0;
  let totalLoanAmount = 0;
  let totalDocumentCharge = 0;
  let totalGst = 0;
  let totalOnline = 0;
  let totalOffline = 0;

  reportData.forEach((row: any) => {
    reportSheet.addRow([
      row.serialNumber,
      formatDate(row.date),
      row.branch,
      row.loanNumber,
      row.customerName,
      LOAN_TYPE_LABEL[row.loanType] ?? row.loanType,
      parseFloat((row.grossWeight || 0).toFixed(3)),
      parseFloat((row.netWeight || 0).toFixed(3)),
      parseFloat((row.loanAmount || 0).toFixed(2)),
      parseFloat((row.documentCharge || 0).toFixed(2)),
      parseFloat((row.gst || 0).toFixed(2)),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/restrict-template-expressions
      row.interestPercentage == null ? row.scheme : `${row.scheme} (${row.interestPercentage}%)`,
      parseFloat((row.online || 0).toFixed(2)),
      parseFloat((row.offline || 0).toFixed(2)),
      parseFloat((row.loanAverage || 0).toFixed(2)),
      row.remarks,
    ]);
    totalGrossWeight += row.grossWeight || 0;
    totalNetWeight += row.netWeight || 0;
    totalLoanAmount += row.loanAmount || 0;
    totalDocumentCharge += row.documentCharge || 0;
    totalGst += row.gst || 0;
    totalOnline += row.online || 0;
    totalOffline += row.offline || 0;
  });

  // Loan average is a rate, so the total row carries the portfolio-wide rate rather than a sum.
  const overallAverage = totalNetWeight > 0 ? totalLoanAmount / totalNetWeight : 0;

  const totalsRow = reportSheet.addRow([
    '',
    '',
    '',
    'Total',
    '',
    '',
    parseFloat(totalGrossWeight.toFixed(3)),
    parseFloat(totalNetWeight.toFixed(3)),
    parseFloat(totalLoanAmount.toFixed(2)),
    parseFloat(totalDocumentCharge.toFixed(2)),
    parseFloat(totalGst.toFixed(2)),
    '',
    parseFloat(totalOnline.toFixed(2)),
    parseFloat(totalOffline.toFixed(2)),
    parseFloat(overallAverage.toFixed(2)),
    '',
  ]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 20 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'organization_loan_report.xlsx';
  link.click();
};

const generateOrganizationLoanReleaseExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('Organization Loan Release Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const headers = [
    'S.No',
    'Date',
    'Branch',
    'Loan Number',
    'Customer Name',
    'Total Amount',
    'Principal',
    'Interest',
    'No of Days',
    'Type',
    'Closure Type',
    'Principal Online',
    'Principal Offline',
    'Interest Online',
    'Interest Offline',
    'Reducing Interest',
    'GW',
    'NW',
    'Release Average',
  ];
  const lastCol = String.fromCharCode(64 + headers.length);

  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalAmount = 0;
  let totalPrincipal = 0;
  let totalInterest = 0;
  let totalPrincipalOnline = 0;
  let totalPrincipalOffline = 0;
  let totalInterestOnline = 0;
  let totalInterestOffline = 0;
  let totalReducingInterest = 0;
  let totalGrossWeight = 0;
  let totalNetWeight = 0;

  reportData.forEach((row: any) => {
    reportSheet.addRow([
      row.serialNumber,
      formatDate(row.date),
      row.branch,
      row.loanNumber,
      row.customerName,
      parseFloat((row.totalAmount || 0).toFixed(2)),
      parseFloat((row.principal || 0).toFixed(2)),
      parseFloat((row.interest || 0).toFixed(2)),
      row.numberOfDays,
      row.loanType,
      row.closureType,
      parseFloat((row.principalOnline || 0).toFixed(2)),
      parseFloat((row.principalOffline || 0).toFixed(2)),
      parseFloat((row.interestOnline || 0).toFixed(2)),
      parseFloat((row.interestOffline || 0).toFixed(2)),
      parseFloat((row.reducingInterest || 0).toFixed(2)),
      parseFloat((row.grossWeight || 0).toFixed(3)),
      parseFloat((row.netWeight || 0).toFixed(3)),
      parseFloat((row.releaseAverage || 0).toFixed(2)),
    ]);
    totalAmount += row.totalAmount || 0;
    totalPrincipal += row.principal || 0;
    totalInterest += row.interest || 0;
    totalPrincipalOnline += row.principalOnline || 0;
    totalPrincipalOffline += row.principalOffline || 0;
    totalInterestOnline += row.interestOnline || 0;
    totalInterestOffline += row.interestOffline || 0;
    totalReducingInterest += row.reducingInterest || 0;
    totalGrossWeight += row.grossWeight || 0;
    totalNetWeight += row.netWeight || 0;
  });

  // Release average is a rate, so the total row carries the portfolio-wide rate rather than a sum.
  const overallAverage = totalNetWeight > 0 ? totalAmount / totalNetWeight : 0;

  const totalsRow = reportSheet.addRow([
    '',
    '',
    '',
    'Total',
    '',
    parseFloat(totalAmount.toFixed(2)),
    parseFloat(totalPrincipal.toFixed(2)),
    parseFloat(totalInterest.toFixed(2)),
    '',
    '',
    '',
    parseFloat(totalPrincipalOnline.toFixed(2)),
    parseFloat(totalPrincipalOffline.toFixed(2)),
    parseFloat(totalInterestOnline.toFixed(2)),
    parseFloat(totalInterestOffline.toFixed(2)),
    parseFloat(totalReducingInterest.toFixed(2)),
    parseFloat(totalGrossWeight.toFixed(3)),
    parseFloat(totalNetWeight.toFixed(3)),
    parseFloat(overallAverage.toFixed(2)),
  ]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 20 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'organization_loan_release_report.xlsx';
  link.click();
};

// A pledge holding jewels from several loans becomes one row per loan in the download, so every
// figure stays a real number the spreadsheet can sum, filter and pivot. Each breakdown entry
// carries that loan's weights and its share of the pledge's amounts, and the pledge-level columns
// repeat on each row. A pledge behind a single loan has nothing to split and stays one row.
const expandByLoan = (row: any): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const breakdown = (row.loanBreakdown ?? []) as any[];
  if (breakdown.length < 2) return [row];
  return breakdown.map((entry) => ({ ...row, ...entry }));
};

const generateObLoanExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('OB Loan Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const headers = [
    'S.No',
    'Date',
    'Branch',
    'Org Loan Number',
    'Pledge ID',
    'OB Number',
    'OB Bank Name',
    'A/C Holder Name',
    'GW',
    'NW',
    'Pledge Amount',
    'Online Principal',
    'Offline Principal',
    'Type',
    'OB Charges (DC)',
    'Remarks',
    'Interest Rate',
  ];
  const lastCol = String.fromCharCode(64 + headers.length);

  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalGrossWeight = 0;
  let totalNetWeight = 0;
  let totalPledgeAmount = 0;
  let totalOnlinePrincipal = 0;
  let totalOfflinePrincipal = 0;
  let totalObCharges = 0;

  reportData.flatMap(expandByLoan).forEach((row: any) => {
    reportSheet.addRow([
      row.serialNumber,
      formatDate(row.date),
      row.branch,
      row.loanNumber,
      row.pledgeId,
      row.obNumber,
      row.obBankName,
      row.accountHolderName,
      parseFloat((row.grossWeight || 0).toFixed(3)),
      parseFloat((row.netWeight || 0).toFixed(3)),
      parseFloat((row.pledgeAmount || 0).toFixed(2)),
      parseFloat((row.onlinePrincipal || 0).toFixed(2)),
      parseFloat((row.offlinePrincipal || 0).toFixed(2)),
      row.loanType,
      parseFloat((row.obCharges || 0).toFixed(2)),
      row.remarks,
      row.interestRate,
    ]);
    totalGrossWeight += row.grossWeight || 0;
    totalNetWeight += row.netWeight || 0;
    totalPledgeAmount += row.pledgeAmount || 0;
    totalOnlinePrincipal += row.onlinePrincipal || 0;
    totalOfflinePrincipal += row.offlinePrincipal || 0;
    totalObCharges += row.obCharges || 0;
  });

  const totalsRow = reportSheet.addRow([
    '',
    '',
    '',
    '',
    'Total',
    '',
    '',
    '',
    parseFloat(totalGrossWeight.toFixed(3)),
    parseFloat(totalNetWeight.toFixed(3)),
    parseFloat(totalPledgeAmount.toFixed(2)),
    parseFloat(totalOnlinePrincipal.toFixed(2)),
    parseFloat(totalOfflinePrincipal.toFixed(2)),
    '',
    parseFloat(totalObCharges.toFixed(2)),
    '',
    '',
  ]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  reportSheet.columns = headers.map(() => ({ width: 20 }));

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'ob_loan_report.xlsx';
  link.click();
};

// One line per jewel, "Jewel (Loan No): Closure". A row expanded to a single loan lists only that
// loan's jewels; rows from before per-jewel closures were reported fall back to the joined labels.
const jewelClosureLines = (row: any, perLoan: boolean): string => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const entries = (row.jewelClosures ?? []) as any[];
  if (entries.length === 0) return row.closureType ?? '';
  return entries
    .filter((entry) => !perLoan || entry.loanKey === row.loanNumber)
    .map((entry) => `${entry.jewel || '-'} (${entry.loanNumber || '-'}): ${entry.closureType || '-'}`)
    .join('\n');
};

const generatePnlExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('P&L Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  reportSheet.mergeCells('A1:C1');
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.mergeCells('A2:C2');
  reportSheet.getCell('A2').value = `P&L Report | Branch: ${branch ?? ''} | ${formatDate(startDate)} to ${formatDate(endDate)}`;
  reportSheet.getCell('A2').font = { bold: true };

  const headerRow = reportSheet.getRow(3);
  headerRow.values = ['S.No', 'Particulars', 'Amount (₹)'];
  headerRow.font = { bold: true };

  reportData.forEach((row: any) => {
    const particulars = row.kind === 'stock' ? `${row.particulars} (${row.grams ?? 0} g)` : row.particulars;
    const excelRow = reportSheet.addRow([row.sNo, particulars, row.kind === 'section' ? '' : row.amount]);
    excelRow.getCell(3).numFmt = '#,##0.00';
    if (row.kind !== 'item') {
      excelRow.font = { bold: true };
    }
    if (row.kind === 'section' || row.kind === 'total' || row.kind === 'stock') {
      excelRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE3F1' } };
      });
    }
  });

  reportSheet.eachRow((row, rowIndex) => {
    if (rowIndex < 3) return;
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
  });

  reportSheet.columns = [{ width: 8 }, { width: 60 }, { width: 20 }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `pnl_report_${startDate ?? ''}_${endDate ?? ''}.xlsx`;
  link.click();
};

const generateObReleaseExcel = async (reportData: any[], branch?: string, startDate?: string, endDate?: string) => {
  const workbook = new ExcelJS.Workbook();
  const reportSheet = workbook.addWorksheet('OB Release Report');

  const organizationName = localStorage.getItem('organizationName') ?? '';
  const headers = [
    'S.No',
    'Date',
    'Branch',
    'Org Loan Number',
    'Pledge ID',
    'OB Number',
    'OB Bank Name',
    'A/C Holder Name',
    'GW',
    'NW',
    'Total Amount',
    'OB Prin',
    'OB Int',
    'Online Principal',
    'Offline Principal',
    'Online Interest',
    'Offline Interest',
    'Reducing Interest',
    'Interest Rate',
    'Type',
    'Closure Type',
    'No of Days',
    'Remarks',
  ];
  const lastCol = String.fromCharCode(64 + headers.length);

  reportSheet.mergeCells(`A1:${lastCol}1`);
  reportSheet.getCell('A1').value = organizationName;
  reportSheet.getCell('A1').font = { bold: true, size: 14 };
  reportSheet.getCell('A1').alignment = { horizontal: 'left', vertical: 'middle' };

  if (branch ?? (startDate ?? endDate)) {
    const headerText = `${branch ? `Branch: ${branch}` : ''}${branch && (startDate && endDate) ? ' | ' : ''}${startDate && endDate ? `From ${formatDate(startDate)} to ${formatDate(endDate)}` : ''}`;
    reportSheet.mergeCells(`A2:${lastCol}2`);
    reportSheet.getCell('A2').value = headerText;
    reportSheet.getCell('A2').font = { bold: true };
    reportSheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle' };
  }

  const headerRowIndex = 3;
  reportSheet.getRow(headerRowIndex).values = headers;
  reportSheet.getRow(headerRowIndex).font = { bold: true };

  let totalGrossWeight = 0;
  let totalNetWeight = 0;
  let totalAmount = 0;
  let totalObPrincipal = 0;
  let totalObInterest = 0;
  let totalOnlinePrincipal = 0;
  let totalOfflinePrincipal = 0;
  let totalOnlineInterest = 0;
  let totalOfflineInterest = 0;
  let totalReducingInterest = 0;

  reportData.flatMap((pledge: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const pledgeHasManyLoans = (pledge.loanBreakdown ?? []).length > 1;
    // Reducing interest belongs to the pledge payment, not to any one loan, so it is written on the
    // pledge's first row only; repeating it on every loan row would inflate the column's sum
    return expandByLoan(pledge).map((row, index) => ({
      row: index === 0 ? row : { ...row, reducingInterest: null },
      pledgeHasManyLoans,
    }));
  }).forEach(({ row, pledgeHasManyLoans }: any) => {
    reportSheet.addRow([
      row.serialNumber,
      formatDate(row.date),
      row.branch,
      row.loanNumber,
      row.pledgeId,
      row.obNumber,
      row.obBankName,
      row.accountHolderName,
      parseFloat((row.grossWeight || 0).toFixed(3)),
      parseFloat((row.netWeight || 0).toFixed(3)),
      parseFloat((row.totalAmount || 0).toFixed(2)),
      parseFloat((row.obPrincipal || 0).toFixed(2)),
      parseFloat((row.obInterest || 0).toFixed(2)),
      parseFloat((row.onlinePrincipal || 0).toFixed(2)),
      parseFloat((row.offlinePrincipal || 0).toFixed(2)),
      parseFloat((row.onlineInterest || 0).toFixed(2)),
      parseFloat((row.offlineInterest || 0).toFixed(2)),
      row.reducingInterest == null ? '' : parseFloat((row.reducingInterest || 0).toFixed(2)),
      row.interestRate,
      row.pledgeType,
      jewelClosureLines(row, pledgeHasManyLoans),
      row.numberOfDays,
      row.remarks,
    ]);
    totalGrossWeight += row.grossWeight || 0;
    totalNetWeight += row.netWeight || 0;
    totalAmount += row.totalAmount || 0;
    totalObPrincipal += row.obPrincipal || 0;
    totalObInterest += row.obInterest || 0;
    totalOnlinePrincipal += row.onlinePrincipal || 0;
    totalOfflinePrincipal += row.offlinePrincipal || 0;
    totalOnlineInterest += row.onlineInterest || 0;
    totalOfflineInterest += row.offlineInterest || 0;
    totalReducingInterest += row.reducingInterest || 0;
  });

  const totalsRow = reportSheet.addRow([
    '',
    '',
    '',
    '',
    'Total',
    '',
    '',
    '',
    parseFloat(totalGrossWeight.toFixed(3)),
    parseFloat(totalNetWeight.toFixed(3)),
    parseFloat(totalAmount.toFixed(2)),
    parseFloat(totalObPrincipal.toFixed(2)),
    parseFloat(totalObInterest.toFixed(2)),
    parseFloat(totalOnlinePrincipal.toFixed(2)),
    parseFloat(totalOfflinePrincipal.toFixed(2)),
    parseFloat(totalOnlineInterest.toFixed(2)),
    parseFloat(totalOfflineInterest.toFixed(2)),
    parseFloat(totalReducingInterest.toFixed(2)),
    '',
    '',
    '',
    '',
    '',
  ]);
  totalsRow.font = { bold: true };
  totalsRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2EB' } };
  });

  reportSheet.eachRow((row, rowIndex) => {
    row.eachCell((cell) => {
      if (rowIndex > 2) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });
  });

  const wideColumns: Record<string, number> = { 'Interest Rate': 38, 'Closure Type': 40 };
  reportSheet.columns = headers.map((header) => ({ width: wideColumns[header] ?? 20 }));

  // Closure Type holds a line per jewel, so it wraps and reads left-aligned
  const closureColumn = headers.indexOf('Closure Type') + 1;
  reportSheet.eachRow((row, rowIndex) => {
    if (rowIndex > headerRowIndex) {
      row.getCell(closureColumn).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'ob_release_report.xlsx';
  link.click();
};

const loanReducer = createSlice({
  name: 'loanReducer',
  reducers: {
    resetUnpledgedLoans(state) {
      state.unpledgedLoans = initialState.unpledgedLoans;
    },
  },
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getLoanTypes.fulfilled, (state, action) => {
      state.metal = action.payload.metals;
      state.type = action.payload.interest;
    });
    builder.addCase(getUnpledgedLoans.fulfilled, (state, action) => {
      state.unpledgedLoans = action.payload;
    });
    builder.addCase(getUndisbursedLoans.fulfilled, (state, action) => {
      state.undisbursedLoans = action.payload;
    });
    builder.addCase(getUnapprovedLoans.fulfilled, (state, action) => {
      state.unapprovedLoans = action.payload;
    });
    builder.addCase(getUnapprovedPayments.fulfilled, (state, action) => {
      state.unapprovedPayments = action.payload;
    });
    builder.addCase(getUnsettledPayments.fulfilled, (state, action) => {
      state.unsettledPayments = action.payload;
    });
    builder.addCase(getReports.fulfilled, (state, action) => {
      if(action.payload.type === 'loan') {
        void generateLoanExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'pledge') {
        void generatePledgeExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'daily') {
        void generateDailyExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'active') {
        void generateActiveExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'refinance') {
        void generateRefinanceExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'expense') {
        void generateExpenseExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'gstr1') {
        void generateGSTR1Excel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'cashbook') {
        void generateCashBookExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'cashflow') {
        void generateCashFlowExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'averageRate') {
        void generateAverageRateExcel(action.payload.responseData, action.payload.branch);
      }
      if(action.payload.type === 'stock') {
        void generateStockExcel(action.payload.responseData, action.payload.branch);
      }
      if(action.payload.type === 'incentives') {
        void generateIncentivesExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'orgLoan') {
        void generateOrganizationLoanExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'orgLoanRelease') {
        void generateOrganizationLoanReleaseExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'obLoan') {
        void generateObLoanExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'obRelease') {
        void generateObReleaseExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
      if(action.payload.type === 'pnl') {
        void generatePnlExcel(action.payload.responseData, action.payload.branch, action.payload.startDate, action.payload.endDate);
      }
    });
    builder.addCase(getRecoveryLoans.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      generatePDF(action.payload.loans, action.payload.title);
    });
  },
});

export const { resetUnpledgedLoans } = loanReducer.actions;
export default loanReducer.reducer;
